import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import ReservationList from '../../components/reservation/ReservationList';
import ReservationStats from '../../components/reservation/ReservationStats';
import PublicReservationLinks from '../../components/reservation/PublicReservationLinks';
import AdminCalendarView from '../../components/reservation/AdminCalendarView';
import TabPanel from '../../components/common/TabPanel';

const ReservationManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, currentOrganization } = useAuth();
  const [tabValue, setTabValue] = React.useState(0);
  const [helpExpanded, setHelpExpanded] = React.useState(true);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!user || !currentOrganization) {
    return null;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('reservation:management.title')}
        </Typography>

        {/* Section d'aide explicative */}
        <Alert
          severity="info"
          icon={<InfoIcon />}
          sx={{ mb: 3 }}
          action={
            <IconButton
              aria-label="toggle help"
              color="inherit"
              size="small"
              onClick={() => setHelpExpanded(!helpExpanded)}
            >
              {helpExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            {t('reservation:management.help.title', 'Comment fonctionne le système de réservation public par flotte ?')}
          </AlertTitle>

          <Collapse in={helpExpanded}>
            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              {t('reservation:management.help.intro', 'Le système de réservation permet aux utilisateurs externes de réserver vos véhicules via un lien public unique par flotte, sans avoir besoin de créer un compte.')}
            </Typography>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <LinkIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('reservation:management.help.step1.title', '1. Lien public par flotte')}
                  secondary={t('reservation:management.help.step1.desc', 'Chaque flotte peut avoir son propre lien public. Activez et partagez ce lien dans l\'onglet "Liens publics".')}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CalendarTodayIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('reservation:management.help.step2.title', '2. Réservation simplifiée')}
                  secondary={t('reservation:management.help.step2.desc', 'Les utilisateurs sélectionnent leurs dates avec des boutons rapides (+2h, demi-journée, journée), choisissent un véhicule disponible, et confirment leur réservation.')}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <EmailIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('reservation:management.help.step3.title', '3. Validation email (optionnelle)')}
                  secondary={t('reservation:management.help.step3.desc', 'Vous pouvez exiger une validation par email et restreindre l\'accès à certains domaines d\'emails (ex: @entreprise.com).')}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('reservation:management.help.step4.title', '4. Gestion centralisée')}
                  secondary={t('reservation:management.help.step4.desc', 'Toutes les réservations apparaissent dans l\'onglet "Liste". Visualisez les disponibilités dans l\'onglet "Calendrier". Les utilisateurs peuvent télécharger un fichier ICS et annuler leur réservation si autorisé.')}
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                💡 {t('reservation:management.help.tip', 'Astuce : Utilisez l\'onglet "Calendrier" pour avoir une vue d\'ensemble des disponibilités de vos flottes en mode jour ou semaine.')}
              </Typography>
            </Box>
          </Collapse>
        </Alert>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={t('reservation:management.tabs.list')} />
            <Tab label={t('reservation:management.tabs.calendar')} />
            <Tab label={t('reservation:management.tabs.statistics')} />
            <Tab label={t('reservation:management.tabs.publicLinks')} />
          </Tabs>
        </Paper>

        <TabPanel value={tabValue} index={0}>
          <ReservationList organizationId={currentOrganization.id} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <AdminCalendarView organizationId={currentOrganization.id} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <ReservationStats organizationId={currentOrganization.id} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <PublicReservationLinks organizationId={currentOrganization.id} />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default ReservationManagementPage;
